import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { ClientSearch } from "@jsq/common-components";
import {
  Flex,
  Text,
  Card,
  Select,
  TextInput,
  Button,
  toast,
  type FileObjectType,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { withClientContext } from "~/apollo/client";
import { useGetArenaLazyQuery } from "~/graphql/mainHooks";
import { DataStudioJobTypeEnum } from "~/graphql/supergraphEnums";
import useDataStudioUploadFiles from "~/hooks/useDataStudioUploadFiles/useDataStudioUploadFiles";
import { type ArenaGridRow } from "../../DataVault/Entities/types";
import { buildOpcoImportJobConfig } from "../utils/buildJobConfiguration";
import { validateEmail } from "../utils/validation";
import { ArenaDetails } from "./components/ArenaDetails";
import { FileManagementSection } from "./components/FileManagementSection";
import * as styles from "./components/styles_vanilla.css";
import { DATA_TYPE_OPTIONS } from "./constants";

const OpcoImport: React.FC = () => {
  const history = useHistory();
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>("test");
  const [selectedArenaId, setSelectedArenaId] = useState<string>("");
  const [selectedArena, setSelectedArena] = useState<ArenaGridRow | null>(null);
  const [adminEmail, setAdminEmail] = useState<string>("");
  const [adminEmailError, setAdminEmailError] = useState<string>("");
  const [arenaName, setArenaName] = useState<string>("");

  const [filesWithTypes, setFilesWithTypes] = useState<
    Array<{ file: FileObjectType; dataType?: string }>
  >([]);
  const [fileUploadKey, setFileUploadKey] = useState<number>(0);
  const [isImporting, setIsImporting] = useState<boolean>(false);

  const [getArena] = useGetArenaLazyQuery({
    context: withClientContext("staff"),
  });

  const { uploadFilesToS3: dataStudioUploadFiles, createJob: dataStudioCreateJob } =
    useDataStudioUploadFiles();

  const isArenaSelected = !!selectedArena;
  const handleArenaSelect = async (arenaId?: string) => {
    if (!arenaId) {
      setSelectedArenaId("");
      setSelectedArena(null);
      return;
    }

    setSelectedArenaId(arenaId);

    try {
      const { data } = await getArena({
        variables: { id: arenaId },
      });

      if (data?.arena) {
        const arenaDetails: ArenaGridRow = {
          id: data.arena.id,
          name: data.arena.name,
          domain: data.arena.domain,
          logoUrl: null,
        };
        setSelectedArena(arenaDetails);
      }
    } catch (_error) {
      setSelectedArenaId("");
      setSelectedArena(null);
    }
  };

  const handleClearValue = () => {
    setSelectedArenaId("");
    setSelectedArena(null);
  };

  const handleEnvironmentChange = (environment: string) => {
    setSelectedEnvironment(environment);
    setSelectedArenaId("");
    setSelectedArena(null);
    setArenaName("");
  };

  const handleSelectArena = () => {
    const trimmedArenaName = arenaName.trim();
    if (trimmedArenaName) {
      const arenaDetails: ArenaGridRow = {
        id: "test-arena",
        name: trimmedArenaName,
        domain: trimmedArenaName,
        logoUrl: null,
      };
      setSelectedArena(arenaDetails);
    }
  };

  const handleChangeArena = () => {
    setSelectedEnvironment("test");
    setSelectedArena(null);
    setSelectedArenaId("");
    setArenaName("");
    setAdminEmail("");
    setAdminEmailError("");
    setFilesWithTypes([]);
    setIsImporting(false);
    setFilesWithTypes([]);
    setFileUploadKey((prev) => prev + 1);
  };

  const selectedDataTypes = filesWithTypes
    .map((item) => item.dataType)
    .filter((dataType): dataType is string => Boolean(dataType));

  const handleFileSelected = (files: FileObjectType[]) => {
    const acceptedFiles = files.filter((file) => file.status === "accepted");

    setFilesWithTypes((prevFilesWithTypes) => {
      const updatedFiles = [...prevFilesWithTypes];

      acceptedFiles.forEach((newFile) => {
        const existingIndex = updatedFiles.findIndex(
          (existingItem) => existingItem.file.file.name === newFile.file.name,
        );

        if (existingIndex >= 0) {
          updatedFiles[existingIndex] = {
            file: newFile,
            dataType: updatedFiles[existingIndex].dataType,
          };
        } else {
          updatedFiles.push({
            file: newFile,
            dataType: "",
          });
        }
      });

      return updatedFiles;
    });
  };

  const isDataTypeDisabled = (dataTypeValue: string, currentFileDataType?: string) => {
    return selectedDataTypes.includes(dataTypeValue) && currentFileDataType !== dataTypeValue;
  };

  const getAvailableDataTypes = (currentFileDataType?: string) => {
    return DATA_TYPE_OPTIONS.filter(
      (dataType) => !isDataTypeDisabled(dataType.value, currentFileDataType),
    );
  };

  const handleDataTypeChange = (
    fileName: string,
    dataType: string | number | string[] | number[],
  ) => {
    setFilesWithTypes((prev) =>
      prev.map((item) =>
        item.file.file.name === fileName ? { ...item, dataType: String(dataType) } : item,
      ),
    );
  };

  const handleRemoveFile = (fileName: string) => {
    setFilesWithTypes((prev) => prev.filter((item) => item.file.file.name !== fileName));
  };

  const handleAdminEmailChange = (email: string) => {
    setAdminEmail(email);
  };

  const handleAdminEmailErrorChange = (error: string) => {
    setAdminEmailError(error);
  };

  const allFilesConfigured =
    filesWithTypes.length > 0 && filesWithTypes.every((f) => f.dataType);
  const hasValidEmail = adminEmail.trim().length > 0 && validateEmail(adminEmail) === "";
  const hasArenaSelected =
    selectedEnvironment === "production" ? selectedArena !== null : arenaName.trim().length > 0;
  const canImport = allFilesConfigured && hasValidEmail && hasArenaSelected;

  const handleImport = async () => {
    setIsImporting(true);

    try {
      const emailError = validateEmail(adminEmail);
      if (emailError) {
        setAdminEmailError(emailError);
        throw new Error("Please enter a valid email address");
      }

      const arenaDomain =
        selectedEnvironment === "production" ? selectedArena?.domain || "" : arenaName.trim();
      const arenaDisplayName =
        selectedEnvironment === "production" ? selectedArena?.name || "" : arenaName.trim();

      if (!arenaDomain) {
        throw new Error("Arena domain is required");
      }

      const filesToUpload = filesWithTypes.map((f) => f.file);
      const uploadResults = await dataStudioUploadFiles({
        files: filesToUpload,
        environment: selectedEnvironment,
        jobType: DataStudioJobTypeEnum.OpcoImport,
        arenaDomain,
        arenaUserEmail: adminEmail.trim(),
      });

      if (!uploadResults || uploadResults.length === 0) {
        throw new Error("File upload failed - no files uploaded");
      }

      const jobConfig = buildOpcoImportJobConfig({
        filesWithTypes: filesWithTypes,
        uploadResults,
        arenaUserEmail: adminEmail.trim(),
        arenaDomain,
      });

      const jobResult = await dataStudioCreateJob({
        jobType: DataStudioJobTypeEnum.OpcoImport,
        arenaDomain,
        arenaName: arenaDisplayName,
        arenaUserEmail: adminEmail.trim(),
        env: selectedEnvironment,
        jobConfig,
      });

      if (!jobResult.success) {
        throw new Error(jobResult.message || "Failed to create OpCo import job");
      }

      toast.success("OpCo import job started successfully!");
      history.push("/data-studio/dashboard");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      toast.error(`Failed to import OpCo data: ${errorMessage}`);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Flex
      data-testid="data-studio-opco-import"
      flexDirection="column"
      gap={Sizes.MD}
      className={styles.container}
    >
      <div className={styles.headerSection}>
        <Card className={styles.headerCard}>
          <Flex alignItems="center" gap={Sizes.MD} className={styles.headerContent}>
            <Icon type="upload" size="large" color={Colors.Primary600} />
            <Text variant="h2">OpCo Import</Text>
          </Flex>
          <Text variant="secondary-md" className={styles.headerDescription}>
            Import operational company data into the selected arena environment
          </Text>
        </Card>
      </div>
      {!isArenaSelected && (
        <Card className={styles.arenaSelectionCard}>
          <Flex flexDirection="column" gap={Sizes.MD}>
            <Flex alignItems="center" gap={Sizes.SM}>
              <Icon type="search" size="medium" color={Colors.Primary600} />
              <Text variant="h4">Arena Selection</Text>
            </Flex>

            <Flex gap={Sizes.SM} alignItems="center">
              <div className={styles.searchInputWrapper}>
                {selectedEnvironment === "test" ? (
                  <TextInput
                    icon="edit"
                    placeholder="Enter arena domain name for test environment"
                    value={arenaName}
                    onChange={(e) => setArenaName(e.target.value)}
                    isFullWidth
                    data-testid="arena-domain-input"
                  />
                ) : (
                  <ClientSearch
                    key={`arena-search-${selectedArenaId || "empty"}`}
                    value={selectedArenaId}
                    onSelect={handleArenaSelect}
                    onSearchClear={handleClearValue}
                    placeholder="Search for an arena..."
                    isFullWidth
                    testId="arena-search"
                  />
                )}
              </div>
              <Select
                value={selectedEnvironment}
                onChange={(value) => {
                  if (typeof value === "string") {
                    handleEnvironmentChange(value);
                  }
                }}
                data-testid="environment-select"
              >
                <Select.Section>
                  <Select.Option value="test">Test</Select.Option>
                  <Select.Option value="production">Production</Select.Option>
                </Select.Section>
              </Select>
              {selectedEnvironment === "test" && (
                <Button
                  variant="primary"
                  onClick={handleSelectArena}
                  disabled={!arenaName.trim()}
                  data-testid="select-arena-button"
                >
                  Select
                </Button>
              )}
            </Flex>
          </Flex>
        </Card>
      )}

      {selectedArena && (
        <ArenaDetails
          selectedArena={selectedArena}
          selectedEnvironment={selectedEnvironment}
          adminEmail={adminEmail}
          adminEmailError={adminEmailError}
          onAdminEmailChange={handleAdminEmailChange}
          onAdminEmailErrorChange={handleAdminEmailErrorChange}
          onChangeArena={handleChangeArena}
        />
      )}

      {selectedArena && (
        <FileManagementSection
          filesWithTypes={filesWithTypes}
          onFileSelected={handleFileSelected}
          onDataTypeChange={handleDataTypeChange}
          onRemoveFile={handleRemoveFile}
          getAvailableDataTypes={getAvailableDataTypes}
          fileUploadKey={fileUploadKey}
        />
      )}

      {filesWithTypes.length > 0 && (
        <div className={styles.submitSection}>
          <div className={styles.buttonGroup}>
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={!canImport}
              isLoading={isImporting}
              className={`${styles.importButton} ${styles.primaryBlackButton}`}
              data-testid="import-button"
            >
              {isImporting ? "Importing..." : "Import OpCo Data"}
            </Button>
          </div>
        </div>
      )}
    </Flex>
  );
};

export default OpcoImport;
